import {
  startNewTrace,
  startSpanManual,
  setContext,
  setTag,
  setUser,
  logger,
  addBreadcrumb,
  captureMessage,
  getCurrentScope,
} from "@sentry/vue";
import type { Router } from "vue-router";
import type { UseCaseDefinition } from "@/http/useCaseDefinitions";
import type { A3dRenderer } from "@axial3d/modelviewer-core";
import {
  initCapture,
  disposeCapture,
  captureFrame,
  markCaptureNeeded,
  getLatestSnapshot,
} from "@/utils/canvasCapture";

const BYTES_PER_KB = 1024;
const BYTES_PER_MB = BYTES_PER_KB * BYTES_PER_KB;
const MS_PER_SECOND = 1000;
const BITS_PER_BYTE = 8;
const BITS_PER_MEGABIT = 1_000_000;
const DEFAULT_TRACKING_INTERVAL_MS = 30000;
const GPU_TAG_MAX_LENGTH = 200;
const SCREEN_WIDTH_SMALL = 768;
const SCREEN_WIDTH_MEDIUM = 1200;
const SCREEN_WIDTH_LARGE = 1920;
const DEFINITION_JSON_MAX_LENGTH = 8000;
const LOW_FPS_THRESHOLD = 20;
const BREADCRUMB_LABEL_MAX_LENGTH = 30;

type Rating = "good" | "needs-improvement" | "poor";

const THRESHOLDS = {
  caseLoad: { good: 5000, poor: 15000 },
  meshLoad: { good: 5000, poor: 15000 },
  dicomLoad: { good: 10000, poor: 30000 },
  meshGeneration: { good: 2000, poor: 10000 },
  aoCalculation: { good: 1000, poor: 5000 },
  timeToInteractive: { good: 10000, poor: 30000 },
  memoryMb: { good: 1000, poor: 2000 },
  downloadSpeedMbps: { good: 10, poor: 5 },
  drawCalls: { good: 100, poor: 500 },
};

function getRating(
  value: number,
  goodThreshold: number,
  poorThreshold: number,
  lowerIsBetter = true,
): Rating {
  if (lowerIsBetter) {
    if (value <= goodThreshold) return "good";
    if (value <= poorThreshold) return "needs-improvement";
    return "poor";
  }
  if (value >= goodThreshold) return "good";
  if (value >= poorThreshold) return "needs-improvement";
  return "poor";
}

interface MemoryMeasurement {
  bytes: number;
  breakdown: Array<{
    bytes: number;
    attribution: Array<{ url?: string; scope?: string }>;
    types: string[];
  }>;
}

declare global {
  interface Performance {
    measureUserAgentSpecificMemory?: () => Promise<MemoryMeasurement>;
  }
}

interface ContextGetters {
  getSceneStats?: () =>
    | { meshCount: number; triangleCount: number; volumeSizeMb?: number }
    | undefined;
  getRenderStats?: () => { drawCalls: number } | undefined;
  getFps?: () => number | undefined;
}

const contextGetters: ContextGetters = {};

let cachedMemory: { memoryMb: number; memoryRating: Rating } | undefined;

async function measureMemory(): Promise<void> {
  if (typeof performance.measureUserAgentSpecificMemory !== "function") {
    return;
  }

  try {
    const measurement =
      (await performance.measureUserAgentSpecificMemory()) as MemoryMeasurement;
    const memoryMb = measurement.bytes / BYTES_PER_MB;
    const { good, poor } = THRESHOLDS.memoryMb;
    cachedMemory = {
      memoryMb,
      memoryRating: getRating(memoryMb, good, poor),
    };
  } catch {
    cachedMemory = undefined;
  }
}

// eslint-disable-next-line no-restricted-globals
let memoryIntervalId: ReturnType<typeof setInterval> | undefined;

export function startMemoryTracking(
  intervalMs: number = DEFAULT_TRACKING_INTERVAL_MS,
): void {
  stopMemoryTracking();
  measureMemory();
  // eslint-disable-next-line no-restricted-globals
  memoryIntervalId = setInterval(measureMemory, intervalMs);
}

export function stopMemoryTracking(): void {
  if (memoryIntervalId !== undefined) {
    clearInterval(memoryIntervalId);
    memoryIntervalId = undefined;
  }
}

function getCachedMemory(): { memoryMb?: number; memoryRating?: Rating } {
  return cachedMemory ?? {};
}

function createPerformanceSpan(
  op: string,
  durationMs: number,
  attributes: Record<string, string | number | undefined>,
  rating?: Rating,
): void {
  const filteredAttrs: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(attributes)) {
    if (value !== undefined) {
      filteredAttrs[key] = value;
    }
  }

  const memorySnapshot = getCachedMemory();
  const sceneStats = contextGetters.getSceneStats?.();
  const renderStats = contextGetters.getRenderStats?.();
  const fps = contextGetters.getFps?.();

  const contextAttrs: Record<string, string | number> = {};
  if (memorySnapshot.memoryMb !== undefined)
    contextAttrs["context.memory_mb"] = Math.round(memorySnapshot.memoryMb);
  if (memorySnapshot.memoryRating)
    contextAttrs["context.memory_rating"] = memorySnapshot.memoryRating;
  if (sceneStats?.meshCount !== undefined)
    contextAttrs["context.mesh_count"] = sceneStats.meshCount;
  if (sceneStats?.triangleCount !== undefined)
    contextAttrs["context.triangle_count"] = sceneStats.triangleCount;
  if (sceneStats?.volumeSizeMb !== undefined)
    contextAttrs["context.volume_size_mb"] = Math.round(
      sceneStats.volumeSizeMb,
    );
  if (renderStats?.drawCalls !== undefined)
    contextAttrs["context.draw_calls"] = renderStats.drawCalls;
  if (fps !== undefined) contextAttrs["context.fps"] = Math.round(fps);

  const allAttrs = {
    ...filteredAttrs,
    ...contextAttrs,
    ...(rating && { rating }),
    "sentry.op": op,
    "sentry.origin": "manual",
  };

  const startTimeSeconds = (Date.now() - durationMs) / MS_PER_SECOND;

  startNewTrace(() => {
    startSpanManual(
      {
        name: op,
        op,
        startTime: startTimeSeconds,
        attributes: allAttrs,
      },
      (span) => {
        span.end();
      },
    );
  });
}

interface GPUInfo {
  renderer: string;
  vendor: string;
  webglVersion: string;
  maxTextureSize: number;
  maxViewportDims: number[];
  extensions: string[];
}

function getGPUInfo(): GPUInfo | undefined {
  const canvas = document.createElement("canvas");
  const gl =
    canvas.getContext("webgl2") ||
    canvas.getContext("webgl") ||
    canvas.getContext("experimental-webgl");

  if (!gl) {
    return undefined;
  }

  const webgl = gl as WebGLRenderingContext;
  const debugInfo = webgl.getExtension("WEBGL_debug_renderer_info");

  const renderer = debugInfo
    ? (webgl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) as string)
    : "unknown";
  const vendor = debugInfo
    ? (webgl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) as string)
    : "unknown";

  const webglVersion = webgl.getParameter(webgl.VERSION) as string;
  const maxTextureSize = webgl.getParameter(webgl.MAX_TEXTURE_SIZE) as number;
  const maxViewportDims = webgl.getParameter(
    webgl.MAX_VIEWPORT_DIMS,
  ) as number[];

  const extensions = webgl.getSupportedExtensions() || [];

  canvas.remove();

  return {
    renderer,
    vendor,
    webglVersion,
    maxTextureSize,
    maxViewportDims: Array.from(maxViewportDims),
    extensions,
  };
}

export function setGPUContext(): void {
  const gpuInfo = getGPUInfo();
  if (gpuInfo) {
    setContext("gpu", {
      renderer: gpuInfo.renderer,
      vendor: gpuInfo.vendor,
      webglVersion: gpuInfo.webglVersion,
      maxTextureSize: gpuInfo.maxTextureSize,
      maxViewportDims: gpuInfo.maxViewportDims.join("x"),
      extensionCount: gpuInfo.extensions.length,
    });

    setTag("gpu.renderer", gpuInfo.renderer.substring(0, GPU_TAG_MAX_LENGTH));
  }
}

function getScreenCategory(): string {
  const width = screen.width;
  if (width < SCREEN_WIDTH_SMALL) return "small";
  if (width < SCREEN_WIDTH_MEDIUM) return "medium";
  if (width < SCREEN_WIDTH_LARGE) return "large";
  return "xlarge";
}

function getDeviceType(): string {
  const ua = navigator.userAgent;
  if (navigator.vendor === "Apple Computer, Inc.") {
    if (ua.includes("iPhone")) return "iphone";
    if (
      ua.includes("iPad") ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
    )
      return "ipad";
    return "mac";
  }
  if (navigator.maxTouchPoints > 1) return "mobile";
  return "desktop";
}

function inferIpadMemory(cores: number): string {
  if (cores <= 4) return "~4GB (est.)";
  if (cores <= 6) return "~6GB (est.)";
  if (cores <= 8) return "~8GB (est.)";
  return "~16GB+ (est.)";
}

function inferIphoneMemory(cores: number): string {
  if (cores <= 2) return "~4GB (est.)";
  if (cores <= 4) return "~6GB (est.)";
  return "~8GB+ (est.)";
}

interface NavigatorExtended extends Navigator {
  deviceMemory?: number;
  connection?: {
    effectiveType?: string;
    downlink?: number;
    saveData?: boolean;
  };
}

export function setBrowserContext(): void {
  const nav = navigator as NavigatorExtended;

  setContext("session_info", {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    cookiesEnabled: navigator.cookieEnabled,
    devicePixelRatio: window.devicePixelRatio,
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight,
    screenWidth: screen.width,
    screenHeight: screen.height,
    colorDepth: screen.colorDepth,
    hardwareConcurrency: navigator.hardwareConcurrency,
    maxTouchPoints: navigator.maxTouchPoints,
    deviceMemory: nav.deviceMemory,
    connectionType: nav.connection?.effectiveType,
    connectionSpeed: nav.connection?.downlink,
    dataSaver: nav.connection?.saveData,
  });

  const deviceType = getDeviceType();
  setTag("device.type", deviceType);
  setTag("device.touch", navigator.maxTouchPoints > 0 ? "yes" : "no");
  setTag("device.screen", getScreenCategory());
  setTag("device.cores", String(navigator.hardwareConcurrency || "unknown"));
  setTag("device.pixelRatio", String(window.devicePixelRatio));

  if (nav.deviceMemory) {
    setTag("device.memory", `${nav.deviceMemory}GB`);
  } else if (
    deviceType === "ipad" ||
    deviceType === "iphone" ||
    deviceType === "mac"
  ) {
    const cores = navigator.hardwareConcurrency;
    if (deviceType === "ipad") {
      setTag("device.memory", inferIpadMemory(cores));
    } else if (deviceType === "iphone") {
      setTag("device.memory", inferIphoneMemory(cores));
    } else {
      setTag("device.memory", "unknown (Apple)");
    }
  }

  if (nav.connection?.effectiveType) {
    setTag("device.connection", nav.connection.effectiveType);
  }
}

export function setCaseContext(
  caseId: string,
  useCaseDefinitionId?: string,
): void {
  setTag("caseId", caseId);
  if (useCaseDefinitionId) {
    setTag("useCaseDefinition", useCaseDefinitionId);
  }
}

function maskEmail(email: string): string {
  const [localPart, domain] = email.split("@");
  if (!localPart || !domain) return "***@unknown";
  const prefix = localPart.substring(0, 3);
  return `${prefix}-***@${domain}`;
}

export function setUserContext(email: string, userId?: string): void {
  const maskedEmail = maskEmail(email);
  setUser({
    id: userId,
    username: maskedEmail,
  });
  setTag("user.domain", email.split("@")[1] || "unknown");
}

export function setUseCaseDefinitionContext(
  definition: UseCaseDefinition,
): void {
  const tag =
    (definition as { tag?: string }).tag ||
    (definition as { id?: string }).id ||
    "unknown";
  setTag("useCaseDefinition.tag", tag);

  setContext("useCaseDefinition", {
    id: (definition as { id?: string }).id,
    tag,
    modules: definition.modules?.join(", "),
    hasAnatomy: !!definition.anatomy,
    hasPresentation: !!definition.presentation,
    labelCount: definition.anatomy?.labelDefinitions?.length ?? 0,
    landmarkCount: definition.anatomy?.landmarkDefinitions?.length ?? 0,
    definition: JSON.stringify(definition).substring(
      0,
      DEFINITION_JSON_MAX_LENGTH,
    ),
  });
}

export function trackDownloadSpeed(
  bytesDownloaded: number,
  durationMs: number,
  resourceType: string,
): void {
  const speedMbps =
    (bytesDownloaded * BITS_PER_BYTE) /
    (durationMs / MS_PER_SECOND) /
    BITS_PER_MEGABIT;
  const { good, poor } = THRESHOLDS.downloadSpeedMbps;
  const rating = getRating(speedMbps, good, poor, false);

  createPerformanceSpan(
    "download",
    durationMs,
    {
      resourceType,
      bytesDownloaded,
      speedMbps: Number(speedMbps.toFixed(2)),
    },
    rating,
  );

  setTag("performance.network_rating", rating);

  if (rating === "poor") {
    logger.warn("Slow download", {
      speedMbps: Number(speedMbps.toFixed(2)),
      resourceType,
      rating,
    });
  }
}

// eslint-disable-next-line no-restricted-globals
let frameRateIntervalId: ReturnType<typeof setInterval> | undefined;

export function startFrameRateTracking(
  getFps: () => number,
  intervalMs: number = DEFAULT_TRACKING_INTERVAL_MS,
): void {
  stopFrameRateTracking();

  contextGetters.getFps = getFps;

  // eslint-disable-next-line no-restricted-globals
  frameRateIntervalId = setInterval(() => {
    const fps = getFps();
    if (fps < LOW_FPS_THRESHOLD) {
      logger.warn("Low FPS", { fps: Math.round(fps) });
    }
  }, intervalMs);
}

export function stopFrameRateTracking(): void {
  if (frameRateIntervalId !== undefined) {
    clearInterval(frameRateIntervalId);
    frameRateIntervalId = undefined;
  }
}

// eslint-disable-next-line no-restricted-globals
let renderStatsIntervalId: ReturnType<typeof setInterval> | undefined;

export function startRenderStatsTracking(
  getRendererInfo: () =>
    | {
        render: { calls: number; triangles: number };
        memory: { textures: number; geometries: number };
      }
    | undefined,
  intervalMs: number = DEFAULT_TRACKING_INTERVAL_MS,
): void {
  stopRenderStatsTracking();

  contextGetters.getRenderStats = () => {
    const info = getRendererInfo();
    return info ? { drawCalls: info.render.calls } : undefined;
  };

  // eslint-disable-next-line no-restricted-globals
  renderStatsIntervalId = setInterval(() => {
    const info = getRendererInfo();
    if (info) {
      const { good, poor } = THRESHOLDS.drawCalls;
      const rating = getRating(info.render.calls, good, poor);
      setTag("performance.draw_calls_rating", rating);
    }
  }, intervalMs);
}

export function stopRenderStatsTracking(): void {
  if (renderStatsIntervalId !== undefined) {
    clearInterval(renderStatsIntervalId);
    renderStatsIntervalId = undefined;
  }
}

export function trackTimeToInteractive(durationMs: number): void {
  const { good, poor } = THRESHOLDS.timeToInteractive;
  const rating = getRating(durationMs, good, poor);

  createPerformanceSpan("time_to_interactive", durationMs, {}, rating);

  setTag("performance.tti_rating", rating);

  if (rating === "poor") {
    logger.warn("Slow time to interactive", {
      durationMs: Math.round(durationMs),
      rating,
    });
  }
}

export function setModuleContext(moduleName: string): void {
  setTag("module", moduleName || "none");
}

export function addUserBreadcrumb(
  message: string,
  category: string,
  data?: Record<string, unknown>,
): void {
  addBreadcrumb({
    message,
    category,
    level: "info",
    data,
  });
}

export function trackMeshLoadTime(meshId: string, durationMs: number): void {
  const { good, poor } = THRESHOLDS.meshLoad;
  const rating = getRating(durationMs, good, poor);

  createPerformanceSpan("mesh.load", durationMs, { meshId }, rating);

  setTag("performance.mesh_load_rating", rating);
}

export function trackDicomLoadTime(stackId: string, durationMs: number): void {
  const { good, poor } = THRESHOLDS.dicomLoad;
  const rating = getRating(durationMs, good, poor);

  createPerformanceSpan("dicom.load", durationMs, { stackId }, rating);

  setTag("performance.dicom_load_rating", rating);
}

export function trackCaseLoadTime(caseId: string, durationMs: number): void {
  const { good, poor } = THRESHOLDS.caseLoad;
  const rating = getRating(durationMs, good, poor);

  createPerformanceSpan("case.load", durationMs, { caseId }, rating);

  setTag("performance.case_load_rating", rating);
}

export function trackMemoryUsage(): void {
  const memory = getCachedMemory();
  if (memory.memoryMb !== undefined && memory.memoryRating) {
    setTag("performance.memory_rating", memory.memoryRating);

    if (memory.memoryRating === "poor") {
      logger.warn("High memory usage", {
        usedMb: Math.round(memory.memoryMb),
        rating: memory.memoryRating,
      });
    }
  }
}

export function registerSceneStatsGetter(
  getter: () =>
    | { meshCount: number; triangleCount: number; volumeSizeMb?: number }
    | undefined,
): void {
  contextGetters.getSceneStats = getter;
}

export function trackMeshGenerationTime(
  durationMs: number,
  labelCount?: number,
  triangleCount?: number,
): void {
  const { good, poor } = THRESHOLDS.meshGeneration;
  const rating = getRating(durationMs, good, poor);

  createPerformanceSpan(
    "mesh.generation",
    durationMs,
    { labelCount, triangleCount },
    rating,
  );

  setTag("performance.meshgen_rating", rating);

  if (rating === "poor") {
    logger.warn("Slow mesh generation", {
      durationMs: Math.round(durationMs),
      labelCount,
      triangleCount,
    });
  }
}

export function trackAOCalculationTime(
  durationMs: number,
  meshCount?: number,
): void {
  const { good, poor } = THRESHOLDS.aoCalculation;
  const rating = getRating(durationMs, good, poor);

  createPerformanceSpan("ao.calculation", durationMs, { meshCount }, rating);

  setTag("performance.ao_rating", rating);

  if (rating === "poor") {
    logger.warn("Slow AO calculation", {
      durationMs: Math.round(durationMs),
      meshCount,
    });
  }
}

let rageClickTimes: number[] = [];
let rageClickElement: EventTarget | undefined;

function trackClick(event: PointerEvent): void {
  const now = Date.now();
  const target = event.target ?? undefined;

  if (target !== rageClickElement) {
    rageClickTimes = [];
    rageClickElement = target;
  }

  rageClickTimes = rageClickTimes.filter((t) => now - t < MS_PER_SECOND);
  rageClickTimes.push(now);

  if (rageClickTimes.length >= 3) {
    addBreadcrumb({
      message: `Rage click detected (${rageClickTimes.length} clicks)`,
      category: "user",
      level: "warning",
      data: {
        clickCount: rageClickTimes.length,
        targetTag: (target as HTMLElement)?.tagName,
        targetId: (target as HTMLElement)?.id,
        targetClass: (target as HTMLElement)?.className,
      },
    });
    logger.warn("Rage click detected", {
      clickCount: rageClickTimes.length,
      targetTag: (target as HTMLElement)?.tagName,
      targetId: (target as HTMLElement)?.id,
    });
    rageClickTimes = [];
  }
}

export function initRageClickTracking(): void {
  document.addEventListener("pointerdown", trackClick);
}

export function trackWebGLContextLoss(canvas: HTMLCanvasElement): void {
  canvas.addEventListener("webglcontextlost", (event) => {
    captureMessage("WebGL context lost", "fatal");
    addBreadcrumb({
      message: "WebGL context lost",
      category: "error",
      level: "fatal",
      data: {
        canvasId: canvas.id,
        canvasWidth: canvas.width,
        canvasHeight: canvas.height,
      },
    });
    logger.error("WebGL context lost", {
      canvasId: canvas.id,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
    });
    event.preventDefault();
  });

  canvas.addEventListener("webglcontextrestored", () => {
    captureMessage("WebGL context restored", "info");
    addBreadcrumb({
      message: "WebGL context restored",
      category: "info",
      level: "info",
    });
    logger.info("WebGL context restored");
  });
}

export function initAutoTracking(router: Router): void {
  router.afterEach((to, from) => {
    addBreadcrumb({
      message: `→ ${to.path}`,
      category: "navigation",
      data: { from: from.path, to: to.path, params: to.params },
    });
  });

  document.addEventListener(
    "click",
    (e) => {
      const el = (e.target as HTMLElement).closest(
        "button, a, [role='button']",
      );
      if (!el) return;

      const label =
        el.getAttribute("data-test") ||
        el.getAttribute("aria-label") ||
        el.getAttribute("title") ||
        el.id ||
        el.textContent?.trim().slice(0, BREADCRUMB_LABEL_MAX_LENGTH) ||
        el.tagName.toLowerCase();

      addBreadcrumb({
        message: label,
        category: "click",
        data: { id: el.id || undefined },
      });
    },
    { capture: true },
  );
}

let canvasCaptureActive = false;
let pointerDownListener: (() => void) | undefined;
let pointerUpListener: (() => void) | undefined;

export function startCanvasCaptureTracking(
  renderer: A3dRenderer,
  setCallback: (cb: ((canvas: HTMLCanvasElement) => void) | undefined) => void,
): void {
  stopCanvasCaptureTracking(renderer, setCallback);
  initCapture(renderer);
  canvasCaptureActive = true;

  pointerDownListener = () => markCaptureNeeded();
  pointerUpListener = () => markCaptureNeeded();
  renderer.domElement.addEventListener("pointerdown", pointerDownListener);
  renderer.domElement.addEventListener("pointerup", pointerUpListener);

  setCallback(() => {
    captureFrame(renderer);

    const snapshot = getLatestSnapshot();
    if (snapshot) {
      const scope = getCurrentScope();
      scope.clearAttachments();
      scope.addAttachment({
        filename: "canvas.jpg",
        data: snapshot,
        contentType: "image/jpeg",
      });
    }
  });
}

export function stopCanvasCaptureTracking(
  renderer: A3dRenderer,
  setCallback: (cb: ((canvas: HTMLCanvasElement) => void) | undefined) => void,
): void {
  if (!canvasCaptureActive) return;
  canvasCaptureActive = false;

  if (pointerDownListener) {
    renderer.domElement.removeEventListener("pointerdown", pointerDownListener);
    pointerDownListener = undefined;
  }
  if (pointerUpListener) {
    renderer.domElement.removeEventListener("pointerup", pointerUpListener);
    pointerUpListener = undefined;
  }

  setCallback(undefined);
  disposeCapture();
  getCurrentScope().clearAttachments();
}

export function setCorrelationId(correlationId: string) {
  setTag("correlationId", correlationId);
}
